import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import safeToast from '../../utils/safeToast';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { normalizeUiLang } from '../../utils/localePreference';
import { requestEmailOtp, verifyEmailOtp } from '../../api/auth';
import { adminLogin } from '../../services/auth';
import { usePublicAuthSettings } from '../../hooks/usePublicAuthSettings';
import { clearNonAdminSessionTokens } from '../../utils/pickAuthToken';
import { dispatchOwnerTokenEvent } from '../../utils/ownerTokenEvents';

export default function AdminLogin({ onLogin }: { onLogin?: (token: string, role: string | null) => void }) {
  const { t, i18n } = useTranslation();
  const lang = normalizeUiLang(i18n.resolvedLanguage || i18n.language);
  const { adminTwoFaEnabled } = usePublicAuthSettings();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTwoFA, setShowTwoFA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const finishLogin = async (otpProof?: string) => {
    const data = await adminLogin(email, password, otpProof);
    if (!data?.token) throw new Error('Login failed');
    clearNonAdminSessionTokens();
    localStorage.setItem('dev_admin_token', data.token);
    localStorage.setItem('dev_admin_role', data.role || '');
    if (data.role === 'superadmin' || data.role === 'basicadmin') {
      localStorage.setItem('dev_admin_initial_mode', 'system_admin');
    }
    dispatchOwnerTokenEvent();
    safeToast('success', t('auth_toast_logged_in'));
    onLogin?.(data.token, data.role || null);
  };

  const sendTwoFACode = async (): Promise<boolean> => {
    try {
      const r = await requestEmailOtp(email, 'admin_login');
      if (r.cooldown && r.cooldown > 0) {
        safeToast('warning', `Please wait ${r.cooldown}s before requesting another code.`);
        return false;
      }
      if (r.sent === false) {
        safeToast('error', 'Could not send the verification email. Check server email settings.');
        return false;
      }
      safeToast('success', `2FA code sent to ${email}. Check your inbox.`);
      return true;
    } catch {
      safeToast('error', 'Failed to send 2FA code.');
      return false;
    }
  };

  const submit = async () => {
    if (!email || !password) {
      safeToast('error', t('auth_toast_login_failed'));
      return;
    }
    setLoading(true);
    try {
      if (adminTwoFaEnabled && !showTwoFA) {
        const sent = await sendTwoFACode();
        if (sent) setShowTwoFA(true);
        return;
      }
      await finishLogin();
    } catch (err: any) {
      const msg = String(err?.data?.error || err?.message || '');
      if (msg === 'otp_required' || msg === 'invalid_otp_proof') {
        safeToast('error', 'Two-factor authentication is required. Enter the code sent to your email.');
        if (!showTwoFA) {
          const sent = await sendTwoFACode();
          if (sent) setShowTwoFA(true);
        }
      } else {
        console.error(err);
        safeToast('error', msg || t('auth_toast_login_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyTwoFA = async () => {
    const code = twoFACode.trim();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const r = await verifyEmailOtp(email, code, 'admin_login');
      if (!r.ok || !r.proof) {
        safeToast('error', 'Invalid verification code.');
        setTwoFACode('');
        return;
      }
      await finishLogin(r.proof);
      setShowTwoFA(false);
      setTwoFACode('');
    } catch (err) {
      console.error(err);
      safeToast('error', t('auth_toast_login_failed'));
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email) {
      safeToast('error', t('auth_toast_enter_email'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/users/password-reset/request', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email }) });
      if (res.ok) {
        safeToast('success', t('auth_toast_reset_sent_short'));
      } else {
        const err = await res.json().catch(() => ({}));
        safeToast('error', err.error || t('auth_toast_reset_failed_short'));
      }
    } catch (err) {
      console.error(err);
      safeToast('error', t('auth_toast_network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 border rounded">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('auth_admin_login_title')}</h3>
        <LanguageSwitcher compact />
      </div>
      <div className="space-y-3">
        {!showTwoFA ? (
          <>
            <div>
              <label className="block text-sm text-gray-700 mb-1">{t('auth_field_email_label')}</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-2 border rounded" placeholder={t('auth_admin_email_placeholder')} />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">{t('auth_field_password_label')}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-2 border rounded" placeholder={t('auth_field_password_placeholder_signin')} />
            </div>
          </>
        ) : (
          <div>
            <label className="block text-sm text-gray-700 mb-1">Verification code</label>
            <input
              value={twoFACode}
              onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full p-2 border rounded tracking-widest text-center text-lg"
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
            />
            <p className="text-xs text-gray-500 mt-2">Enter the 6-digit code sent to {email}</p>
          </div>
        )}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={showTwoFA ? verifyTwoFA : submit}
            disabled={loading || (showTwoFA && twoFACode.length !== 6)}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            {loading ? t('auth_admin_signing_in') : showTwoFA ? 'Verify & Sign In' : (adminTwoFaEnabled ? 'Send Code' : t('auth_admin_sign_in'))}
          </button>
          {!showTwoFA && (
            <button type="button" onClick={forgotPassword} disabled={loading} className="text-sm text-blue-600 hover:underline">
              {t('auth_admin_forgot_password')}
            </button>
          )}
          {showTwoFA && (
            <button type="button" onClick={() => { setShowTwoFA(false); setTwoFACode(''); }} className="text-sm text-gray-600 hover:underline">
              Back
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
